/**
 * Recherche des radars situés le long d'une trace.
 *
 * La base compte jusqu'à une vingtaine de milliers de points selon les pays
 * retenus, et une trace peut en compter deux cent mille : projeter chaque
 * radar sur chaque segment ferait un produit cartésien qui bloquerait
 * l'interface plusieurs secondes. Deux filtres l'évitent, dans cet ordre :
 *
 *  - une **boîte englobante élargie du seuil** écarte l'écrasante majorité des
 *    radars en un seul passage linéaire ;
 *  - une **grille posée sur les survivants** remplace le produit cartésien qui
 *    restait. La trace n'est plus parcourue une fois par radar mais une seule
 *    fois en tout : chaque segment n'interroge que les cases que sa boîte
 *    touche.
 *
 * Le résultat est identique à celui du parcours complet, et pas seulement
 * « équivalent » : les deux filtres ne servent qu'à éviter du calcul, jamais à
 * décider — c'est la projection, et elle seule, qui retient ou écarte un
 * radar. Ce qui compte est donc que **rien de ce qui est à portée ne soit
 * écarté d'avance**, et les marges y veillent avec 50 % de rabiot.
 *
 * Ce que rend la projection est **un résultat par passage** et non un par
 * radar : une boucle à tours, un aller-retour, repassent devant le même radar,
 * et un seul marqueur priverait le cycliste du roadbook et de l'alerte
 * d'approche à toutes les fois suivantes. Les projections d'un même radar sont
 * donc gardées toutes, puis découpées par `clusterPassages` — la découpe même
 * que sert le dialogue « Corriger la position ».
 *
 * Référence : `RadarTrackChecker.kt`, qui s'en tient au premier filtre et
 * confie le reste à `Dispatchers.Default` — écart assumé, documenté au
 * `README.md`.
 */

import {
  METERS_PER_DEGREE_LAT,
  bboxMargins,
  haversine,
  projectOnSegment,
  toRadians,
} from './geo'
import { clusterPassages } from './track'
import type { DistanceCandidate, GpxTrack } from './track'
import type { RadarPoint } from './radars'

/** Distance au tracé en deçà de laquelle un radar est retenu. */
export const RADAR_THRESHOLD_M = 100

/**
 * Écart curviligne au-delà duquel deux projections font deux passages.
 *
 * Un radar posé au bord de la route se projette sur chacun des segments qui
 * l'encadrent : sans fusion, une seule vue en donnerait plusieurs marqueurs.
 * Au-delà de cet écart, en revanche, la trace est réellement repassée devant
 * lui — un tour de circuit, une branche d'aller-retour — et le radar mérite un
 * marqueur de plus, sans quoi ni le roadbook ni l'alerte d'approche ne
 * l'annonceraient aux tours suivants.
 *
 * Valeur reprise de `RadarTrackChecker.PASSAGE_GAP_METERS` (300 m), et non du
 * double du seuil que `projectPointAll` prend par défaut : les 200 m qui en
 * découleraient sépareraient un rond-point un peu large en deux passages.
 */
export const RADAR_PASSAGE_GAP_M = 300

/**
 * Côté d'une case de la grille, en mètres.
 *
 * Un compromis entre deux coûts qui varient en sens inverse : une case plus
 * petite fait interroger plus de cases par segment, une case plus grande fait
 * projeter plus de radars par segment. Le creux est large, et la valeur est
 * prise dedans plutôt qu'à son bord — mesuré sur 200 000 points, à 300 puis
 * 3 000 radars : 88 et 70 ms à 125 m, 64 et 45 à 500 m, 64 et 45 à 1 km, 63 et
 * 61 à 2 km, 72 et 221 à 5 km. C'est la seconde pente qui coûte cher, et c'est
 * elle qu'on fuit : une case trop grande réunit des radars qu'il faudra
 * projeter un par un pour chaque segment qui la touche.
 */
const CELL_SIZE_M = 1000

/**
 * Nombre de cases au-delà duquel un segment balaie tous les survivants.
 *
 * Un saut GPS — sortie de tunnel, relevé aberrant — produit un segment de
 * plusieurs centaines de kilomètres, dont la boîte couvre des dizaines de
 * milliers de cases vides. Les énumérer coûterait bien plus cher que de
 * reprendre la liste entière pour ce segment-là.
 */
const MAX_CELLS_PER_SEGMENT = 64

export interface RadarHit {
  readonly radar: RadarPoint
  /**
   * Abscisse curviligne brute du passage, en mètres, prise à sa projection la
   * plus proche du radar.
   */
  readonly distance: number
}

/**
 * Radars à moins de `threshold` mètres du tracé, triés par position.
 *
 * Un radar croisé plusieurs fois rend un résultat par passage — voir
 * `RADAR_PASSAGE_GAP_M`. Les doublons, eux, ne sont pas écartés : deux radars
 * distincts au même endroit — les deux sens d'une même route — sont deux
 * radars pour le cycliste.
 */
export function findRadarsNearTrack(
  track: GpxTrack,
  radars: readonly RadarPoint[],
  threshold = RADAR_THRESHOLD_M,
): RadarHit[] {
  const points = track.allPoints
  if (points.length === 0 || radars.length === 0) return []

  let minLat = Infinity
  let maxLat = -Infinity
  let minLon = Infinity
  let maxLon = -Infinity

  for (const point of points) {
    if (point.lat < minLat) minLat = point.lat
    if (point.lat > maxLat) maxLat = point.lat
    if (point.lon < minLon) minLon = point.lon
    if (point.lon > maxLon) maxLon = point.lon
  }

  /*
   * Les marges sont prises à la latitude la plus haute de la trace, pas à
   * celle de chaque radar comme le fait la projection point par point.
   *
   * Une marge en longitude vaut la marge en latitude divisée par le cosinus :
   * elle rétrécit vers les pôles. La prendre au milieu de la trace donnerait,
   * sur une trace couvrant vingt degrés, une marge trop courte à son extrémité
   * nord — un radar à portée pourrait alors être écarté avant projection, et
   * la grille cesserait d'être un simple raccourci.
   */
  const worstLat = Math.max(Math.abs(minLat), Math.abs(maxLat))
  const { latMargin, lonMargin } = bboxMargins(worstLat, threshold)

  // Pré-filtre par la boîte de la trace, élargie du seuil : sans cet
  // élargissement, un radar situé juste au-delà d'une extrémité mais bien à
  // portée serait écarté d'emblée.
  const survivors: RadarPoint[] = []
  for (const radar of radars) {
    if (
      radar.lat < minLat - latMargin ||
      radar.lat > maxLat + latMargin ||
      radar.lon < minLon - lonMargin ||
      radar.lon > maxLon + lonMargin
    ) {
      continue
    }
    survivors.push(radar)
  }

  if (survivors.length === 0) return []

  const latCell = CELL_SIZE_M / METERS_PER_DEGREE_LAT
  const lonCell = latCell / Math.max(Math.cos(toRadians(worstLat)), 0.01)

  // Origine prise sur la boîte élargie : les indices de case restent positifs
  // et petits, ce qui permet de composer une clé numérique unique.
  const originLat = minLat - latMargin
  const originLon = minLon - lonMargin

  const grid = new Map<number, number[]>()
  for (let index = 0; index < survivors.length; index += 1) {
    const radar = survivors[index]!
    const key = cellKey(
      Math.floor((radar.lat - originLat) / latCell),
      Math.floor((radar.lon - originLon) / lonCell),
    )
    const bucket = grid.get(key)
    if (bucket) bucket.push(index)
    else grid.set(key, [index])
  }

  // Toutes les projections retenues, radar par radar : c'est leur découpe en
  // passages qui décide du nombre de marqueurs, et elle ne peut se faire
  // qu'une fois la trace entièrement parcourue.
  const projections: (DistanceCandidate[] | null)[] = new Array(
    survivors.length,
  ).fill(null)
  const cumulative = track.cumulativeDistances

  // Réutilisé d'un segment à l'autre : deux cent mille segments valent deux
  // cent mille allocations, et le ramasse-miettes se paie en à-coups visibles.
  const candidates: number[] = []

  let flatBase = 0

  for (const segment of track.segments) {
    const segmentPoints = segment.points

    for (let i = 0; i < segmentPoints.length - 1; i += 1) {
      const p1 = segmentPoints[i]!
      const p2 = segmentPoints[i + 1]!

      const boxMinLat = Math.min(p1.lat, p2.lat) - latMargin
      const boxMaxLat = Math.max(p1.lat, p2.lat) + latMargin
      const boxMinLon = Math.min(p1.lon, p2.lon) - lonMargin
      const boxMaxLon = Math.max(p1.lon, p2.lon) + lonMargin

      const rowMin = Math.floor((boxMinLat - originLat) / latCell)
      const rowMax = Math.floor((boxMaxLat - originLat) / latCell)
      const colMin = Math.floor((boxMinLon - originLon) / lonCell)
      const colMax = Math.floor((boxMaxLon - originLon) / lonCell)

      candidates.length = 0
      const cells = (rowMax - rowMin + 1) * (colMax - colMin + 1)

      if (cells > MAX_CELLS_PER_SEGMENT) {
        for (let index = 0; index < survivors.length; index += 1) {
          candidates.push(index)
        }
      } else {
        for (let row = rowMin; row <= rowMax; row += 1) {
          for (let col = colMin; col <= colMax; col += 1) {
            const bucket = grid.get(cellKey(row, col))
            if (bucket === undefined) continue
            for (const index of bucket) candidates.push(index)
          }
        }
      }

      for (const index of candidates) {
        const radar = survivors[index]!

        // Le même pré-filtre que la projection point par point : la case est
        // plus large que la boîte du segment, tout ce qu'elle réunit n'est pas
        // à portée.
        if (
          radar.lat < boxMinLat ||
          radar.lat > boxMaxLat ||
          radar.lon < boxMinLon ||
          radar.lon > boxMaxLon
        ) {
          continue
        }

        const proj = projectOnSegment(
          radar.lat,
          radar.lon,
          p1.lat,
          p1.lon,
          p2.lat,
          p2.lon,
        )
        const projDist = haversine(radar.lat, radar.lon, proj.lat, proj.lon)
        if (projDist > threshold) continue

        const from = cumulative[flatBase + i]!
        const to = cumulative[flatBase + i + 1]!
        const candidate = {
          distance: from + proj.t * (to - from),
          projDist,
        }

        const found = projections[index]
        if (found) found.push(candidate)
        else projections[index] = [candidate]
      }
    }

    // Incrémenté même pour les segments de moins de deux points, sans quoi
    // l'indexation à plat se désaligne des distances cumulées.
    flatBase += segmentPoints.length
  }

  const hits: RadarHit[] = []
  for (let index = 0; index < survivors.length; index += 1) {
    const found = projections[index]
    if (!found) continue
    for (const passage of clusterPassages(found, RADAR_PASSAGE_GAP_M)) {
      hits.push({ radar: survivors[index]!, distance: passage.distance })
    }
  }

  // Tri stable : à égale distance, l'ordre de la base est conservé.
  return hits.sort((a, b) => a.distance - b.distance)
}

/**
 * Clé d'une case, ligne et colonne réunies en un seul nombre.
 *
 * Une chaîne `${row}:${col}` coûterait une allocation par case interrogée, et
 * il y en a jusqu'à quatre par segment sur deux cent mille segments. Le facteur
 * laisse de la place à toute boîte réelle : à un kilomètre par case, il faudrait
 * une trace de soixante-sept millions de kilomètres pour l'atteindre.
 */
function cellKey(row: number, col: number): number {
  return row * 67_108_864 + col
}
